/**
 * Gestión → Administración → Comprobantes (`/gestion/administracion/comprobantes`).
 *
 * Puerto de `Administration::invoices` + `views/menu/administrador/comprobantes.php`
 * (el bundle `credit.invoices.bundle.js`): el mantenimiento de los comprobantes
 * electrónicos administrativos.
 *
 * Qué se porta:
 * - El **listado** de comprobantes con su cliente, vendedor, serie y número,
 *   total y estado en SUNAT, y el resumen por estado.
 * - **Aprobar seleccionados**: los comprobantes elegidos pasan a la cola de
 *   SUNAT (`Queue`).
 * - El **detalle** de la venta: condición de pago, líneas, movimientos de cuenta
 *   y **actualización de importes** (`edition_post`), con la retención del
 *   cliente.
 * - **Consultar estado** del comprobante en el servicio de facturación.
 * - **Anular** dentro del plazo (`config.dias_maximos_anular`) o **anular
 *   mediante nota de crédito** cuando ya pasó, con su motivo y su serie.
 * - Las **descargas y la impresión** del comprobante, que las sirve el servicio
 *   de facturación con el `external_id` (A4, A5, 80 mm y 50 mm, PDF, XML y CDR).
 *
 * Nota: la «Cola de Trabajos» del original arrancaba y paraba el proceso
 * `queue/listen` del panel anterior (`Services::start_queue`); eso es
 * infraestructura del servidor, no una pantalla, así que aquí sólo se muestra su
 * estado a través del mantenimiento (los comprobantes aprobados quedan en
 * `Queue`).
 */

import {
  AlertTriangle,
  BadgeX,
  Eye,
  FilePlus2,
  Info,
  Lock,
  Printer,
  RefreshCw,
  Search,
  Send,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MenuAcciones } from '@/components/ui/menu-acciones';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { AdministracionNav } from '../components/administracion-nav';
import { ComprobanteDetalleDialog } from '../components/comprobante-detalle-dialog';
import { ComprobanteNotaDialog } from '../components/comprobante-nota-dialog';
import { fechaCorta, hoyIso, monto } from '../gestion-formato';
import {
  useAnularComprobante,
  useAprobarComprobantes,
  useComprobantes,
  useConsultarEstado,
  useRepararEstados,
} from '../comprobantes.api';
import type { ComprobanteAdministrativo, FiltrosComprobantes } from '../comprobantes.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tonos del distintivo del estado en SUNAT. */
const TONOS: Record<string, 'success' | 'danger' | 'warning' | 'brand' | 'neutral'> = {
  Aceptado: 'success',
  Registrado: 'success',
  Queue: 'brand',
  Processing: 'brand',
  'En Validación': 'warning',
  Pendiente: 'warning',
  'Por anular': 'warning',
  Rechazado: 'danger',
  Error: 'danger',
  Anulado: 'danger',
  'Nota de Credito': 'neutral',
};

/** Estados que ofrece el filtro (los del original más los del flujo nuevo). */
const ESTADOS = [
  'En Validación',
  'Queue',
  'Processing',
  'Aceptado',
  'Registrado',
  'Rechazado',
  'Pendiente',
  'Por anular',
  'Anulado',
  'Nota de Credito',
];

export function ComprobantesPage() {
  useDocumentTitle('Gestión · Comprobantes');

  const navegar = useNavigate();

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [estado, setEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [detalle, setDetalle] = useState<number | null>(null);
  const [nota, setNota] = useState<number | null>(null);
  const [confirmarAprobacion, setConfirmarAprobacion] = useState(false);
  const [anular, setAnular] = useState<number | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosComprobantes>(
    () => ({
      desde: desde || undefined,
      hasta: hasta || undefined,
      estado: estado || undefined,
      search: busqueda.trim() || undefined,
      limit: 300,
    }),
    [desde, hasta, estado, busqueda],
  );

  const consulta = useComprobantes(filtros);
  const aprobar = useAprobarComprobantes();
  const reparar = useRepararEstados();
  const consultarEstado = useConsultarEstado();
  const anularComprobante = useAnularComprobante(anular);

  const datos = consulta.data;
  const comprobantes = datos?.comprobantes ?? [];
  const urlServicio = datos?.url_facturacion ?? null;

  // La API devuelve hasta 300 comprobantes; en pantalla se ven de diez en diez.
  const paginacion = usePaginacion(comprobantes, { clave: filtros });

  /** Marca o desmarca un comprobante para aprobarlo. */
  const alternar = (ventaId: number) => {
    setSeleccion((previo) => {
      const copia = new Set(previo);

      if (copia.has(ventaId)) copia.delete(ventaId);
      else copia.add(ventaId);

      return copia;
    });
  };

  /** Aprueba los comprobantes marcados. */
  const aprobarSeleccionados = () => {
    setError(null);
    aprobar.mutate([...seleccion], {
      onSuccess: (respuesta) => {
        setConfirmarAprobacion(false);
        setSeleccion(new Set());
        setAviso(respuesta.mensaje);
      },
      onError: (fallo) => {
        setConfirmarAprobacion(false);
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron aprobar.');
      },
    });
  };

  /** Consulta el estado del comprobante en el servicio de facturación. */
  const consultar = (comprobante: ComprobanteAdministrativo) => {
    setError(null);
    consultarEstado.mutate(comprobante.venta_id, {
      onSuccess: (respuesta) => setAviso(`${respuesta.documento ?? ''}: ${respuesta.mensaje}`),
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo consultar el estado.'),
    });
  };

  /** Anula el comprobante (dentro del plazo lo acepta el servicio directo). */
  const anularSeleccionado = () => {
    setError(null);
    anularComprobante.mutate(motivoAnulacion, {
      onSuccess: (respuesta) => {
        setAnular(null);
        setMotivoAnulacion('');
        setAviso(respuesta.mensaje);
      },
      onError: (fallo) => {
        setAnular(null);
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo anular el comprobante.');
      },
    });
  };

  /** Devuelve a la cola los comprobantes que quedaron en error. */
  const repararEstados = () => {
    setError(null);
    reparar.mutate(undefined, {
      onSuccess: (respuesta) => setAviso(respuesta.mensaje),
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron reparar los estados.'),
    });
  };

  /** Enlaces del servicio de facturación para un comprobante. */
  const enlaces = (comprobante: ComprobanteAdministrativo) => {
    if (!urlServicio || !comprobante.external_id) return [];

    const base = urlServicio.replace(/\/api\/?$/, '');
    const id = comprobante.external_id;

    return [
      { etiqueta: 'Imprimir A4', href: `${base}/print/document/${id}/a4` },
      { etiqueta: 'Imprimir A5', href: `${base}/print/document/${id}/a5` },
      { etiqueta: '80 mm', href: `${base}/print/document/${id}/ticket` },
      { etiqueta: '50 mm', href: `${base}/print/document/${id}/ticket_50` },
    ];
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Comprobantes administrativos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Aprobar, consultar, corregir importes, anular o emitir la nota de crédito de los
            comprobantes electrónicos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            leftIcon={<FilePlus2 className="size-4" aria-hidden="true" />}
            onClick={() => navegar('/gestion/administracion/comprobantes/crear')}
          >
            Crear documento
          </Button>

          <Button
            variant="outline"
            size="sm"
            loading={reparar.isPending}
            leftIcon={<Wrench className="size-4" aria-hidden="true" />}
            onClick={repararEstados}
          >
            Reparar estados
          </Button>

          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <AdministracionNav active="comprobantes" />

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {datos ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { etiqueta: 'Comprobantes', valor: String(datos.resumen.total) },
            { etiqueta: 'Con los filtros', valor: String(datos.resumen.filtrados) },
            {
              etiqueta: 'En validación',
              valor: String(datos.resumen.estados['En Validación'] ?? 0),
            },
            { etiqueta: 'En cola (Queue)', valor: String(datos.resumen.estados.Queue ?? 0) },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Desde"
          type="date"
          value={desde}
          max={hoyIso()}
          onChange={(evento) => setDesde(evento.target.value)}
        />
        <Input
          label="Hasta"
          type="date"
          value={hasta}
          max={hoyIso()}
          onChange={(evento) => setHasta(evento.target.value)}
        />
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Estado en SUNAT</span>
          <Select
            className={CONTROL}
            value={estado}
            onChange={(evento) => setEstado(evento.target.value)}
          >
            <option value="">Todos</option>
            {ESTADOS.map((nombre) => (
              <option key={nombre} value={nombre}>
                {nombre}
              </option>
            ))}
          </Select>
        </label>
        <Input
          label="Buscar"
          placeholder="Cliente, documento, vendedor o serie"
          leftIcon={<Search className="size-4" aria-hidden="true" />}
          value={busqueda}
          onChange={(evento) => setBusqueda(evento.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-500">
          {datos
            ? `${comprobantes.length} de ${datos.total} comprobante(s)${
                datos.dias_maximos_anular ? ` · se anulan directo hasta ${datos.dias_maximos_anular} días` : ''
              }`
            : 'Cargando…'}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!comprobantes.some((fila) => fila.puede_aprobar)}
            onClick={() =>
              setSeleccion(
                new Set(comprobantes.filter((fila) => fila.puede_aprobar).map((fila) => fila.venta_id)),
              )
            }
          >
            Seleccionar aprobables
          </Button>
          <Button variant="ghost" size="sm" disabled={!seleccion.size} onClick={() => setSeleccion(new Set())}>
            Limpiar
          </Button>
          <Button
            size="sm"
            disabled={!seleccion.size}
            leftIcon={<Send className="size-4" aria-hidden="true" />}
            onClick={() => setConfirmarAprobacion(true)}
          >
            Aprobar seleccionados ({seleccion.size})
          </Button>
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron leer los comprobantes">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {datos ? (
        <>
        <div className="overflow-x-auto rounded-card border border-ink-100">
          {/*
           * Sin ancho mínimo: la tabla se reparte el ancho disponible y las
           * celdas largas (cliente, vendedor) se recortan con su `title`, para
           * que no haya que desplazarse a la derecha. El número, la serie, la
           * emisión y la antigüedad van juntos en una sola columna de dos
           * líneas.
           */}
          <table className="w-full table-fixed border-collapse text-xs">
            <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
              <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-500"
                    aria-label="Seleccionar los comprobantes aprobables"
                    disabled={!comprobantes.some((fila) => fila.puede_aprobar)}
                    checked={
                      comprobantes.some((fila) => fila.puede_aprobar) &&
                      comprobantes.filter((fila) => fila.puede_aprobar).every((fila) => seleccion.has(fila.venta_id))
                    }
                    onChange={(evento) => {
                      if (evento.target.checked) {
                        setSeleccion(
                          new Set(
                            comprobantes.filter((fila) => fila.puede_aprobar).map((fila) => fila.venta_id),
                          ),
                        );
                      } else {
                        setSeleccion(new Set());
                      }
                    }}
                  />
                </th>
                <th className="w-[112px] px-2 py-2">Comprobante</th>
                <th className="px-2 py-2">Cliente</th>
                <th className="hidden w-[124px] px-2 py-2 md:table-cell">Vendedor</th>
                <th className="w-[78px] px-2 py-2 text-right">Total</th>
                <th className="w-[106px] px-2 py-2">SUNAT</th>
                <th className="w-[104px] px-2 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginacion.visibles.map((fila) => (
                <tr
                  key={fila.venta_id}
                  className={cn(
                    'border-t border-ink-100',
                    seleccion.has(fila.venta_id) && 'bg-brand-50/40',
                  )}
                >
                  <td className="px-2 py-2 align-top">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand-500"
                      aria-label={`Seleccionar el comprobante ${fila.documento ?? fila.venta_id}`}
                      disabled={!fila.puede_aprobar}
                      checked={seleccion.has(fila.venta_id)}
                      onChange={() => alternar(fila.venta_id)}
                    />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <span className="block truncate font-semibold text-ink-800">
                      #{fila.venta_id}
                      {fila.documento ? <span className="text-ink-500"> · {fila.documento}</span> : null}
                    </span>
                    <span className="block text-[11px] whitespace-nowrap text-ink-500">
                      {fechaCorta(fila.fecha_emision)}
                      {fila.antiguedad > 0 ? ` · ${fila.antiguedad} d` : ''}
                    </span>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <span className="block truncate" title={fila.cliente ?? ''}>
                      {fila.cliente ?? '—'}
                    </span>
                    <span className="block truncate text-[11px] text-ink-500">
                      {fila.numero_doc ?? ''}
                    </span>
                  </td>
                  <td className="hidden px-2 py-2 align-top md:table-cell">
                    <span className="block truncate" title={fila.vendedor || '—'}>
                      {fila.vendedor || '—'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right align-top">
                    <span className="block whitespace-nowrap">{monto(fila.total)}</span>
                    {fila.tiene_retencion || fila.tiene_detraccion ? (
                      <span className="mt-1 flex flex-wrap justify-end gap-1">
                        {fila.tiene_retencion ? (
                          <Badge variant="warning" title="Tiene retención">
                            Retención
                          </Badge>
                        ) : null}
                        {fila.tiene_detraccion ? (
                          <Badge variant="brand" title="Operación sujeta a detracción">
                            Detracción
                          </Badge>
                        ) : null}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Badge variant={TONOS[fila.estado_sunat ?? ''] ?? 'neutral'}>
                      {fila.estado_sunat ?? 'Sin comprobante'}
                    </Badge>
                    {fila.anulado && fila.estado_sunat !== 'Anulado' ? (
                      <span className="mt-1 block text-[11px] text-rose-600">Anulado</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Ver el detalle de la venta"
                        aria-label="Ver el detalle de la venta"
                        onClick={() => setDetalle(fila.venta_id)}
                      >
                        <Eye className="size-4" aria-hidden="true" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        title="Consultar el estado en el servicio de facturación"
                        aria-label="Consultar el estado en el servicio de facturación"
                        disabled={consultarEstado.isPending}
                        onClick={() => consultar(fila)}
                      >
                        <RefreshCw className="size-4" aria-hidden="true" />
                      </Button>

                      <MenuAcciones
                        acciones={[
                          ...(fila.puede_anular
                            ? [
                                {
                                  etiqueta: 'Anular comprobante',
                                  peligrosa: true,
                                  icono: <BadgeX className="size-3.5" aria-hidden="true" />,
                                  onClick: () => {
                                    setMotivoAnulacion('');
                                    setAnular(fila.venta_id);
                                  },
                                },
                              ]
                            : []),
                          ...(fila.puede_nota
                            ? [
                                {
                                  etiqueta: 'Nota de crédito',
                                  peligrosa: true,
                                  icono: <BadgeX className="size-3.5" aria-hidden="true" />,
                                  onClick: () => setNota(fila.venta_id),
                                },
                              ]
                            : []),
                          ...enlaces(fila).map((enlace) => ({
                            etiqueta: enlace.etiqueta,
                            href: enlace.href,
                            icono: <Printer className="size-3.5" aria-hidden="true" />,
                          })),
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {comprobantes.length === 0 ? (
                <tr>
                  <td className="px-2 py-6 text-center text-ink-500" colSpan={7}>
                    No hay comprobantes que cumplan los filtros.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Paginador {...paginacion} etiqueta="comprobantes" className="mt-3" />
        </>
      ) : null}

      <Alert variant="info" title="Cómo funciona el envío a SUNAT">
        <span className={ICONO_EN_ALERTA}>
          <Info className="size-4" aria-hidden="true" />
          Aprobar un comprobante lo deja en <b>Queue</b>, la cola que la ticketera del servidor
          envía a SUNAT; «Consultar estado» pregunta al servicio de facturación y guarda lo que
          responda. Dentro de {datos?.dias_maximos_anular ?? 7} días se anula directo y, si ya
          pasaron, se anula mediante <b>nota de crédito</b>.
        </span>
      </Alert>

      <Alert variant="info" title="Pendiente con candado">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              «Actualizar fechas para reenvío» (`actualizar_fechas_comprobante`), que sólo aplica a
              los comprobantes en <b>En Validación</b>.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              La «Cola de Trabajos» del original arrancaba y detenía el proceso `queue/listen` del
              panel anterior; aquí sólo se consulta su estado con el servicio del panel.
            </span>
          </li>
        </ul>
      </Alert>

      <ComprobanteDetalleDialog
        key={`detalle-${detalle ?? 0}`}
        id={detalle}
        onClose={() => setDetalle(null)}
        onAviso={setAviso}
      />

      <ComprobanteNotaDialog
        key={`nota-${nota ?? 0}`}
        id={nota}
        onClose={() => setNota(null)}
        onAviso={(mensaje) => setAviso(mensaje)}
      />

      <Dialog
        open={anular !== null}
        onClose={() => setAnular(null)}
        size="md"
        title="Anular el comprobante"
        description="Se enviará la anulación al servicio de facturación y, si la acepta, el comprobante quedará anulado."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setAnular(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={!motivoAnulacion.trim()}
              loading={anularComprobante.isPending}
              leftIcon={<BadgeX className="size-4" aria-hidden="true" />}
              onClick={anularSeleccionado}
            >
              Anular
            </Button>
          </>
        }
      >
        <Input
          label="Motivo de la anulación"
          placeholder="Ej: error en los datos del cliente"
          value={motivoAnulacion}
          onChange={(evento) => setMotivoAnulacion(evento.target.value)}
        />
      </Dialog>

      <ConfirmDialog
        open={confirmarAprobacion}
        title="Aprobar los comprobantes"
        description={`Se enviarán ${seleccion.size} comprobante(s) a la cola de SUNAT (Queue). Los que ya estén aceptados, anulados o notados no se tocan.`}
        confirmLabel="Aprobar"
        tone="primary"
        loading={aprobar.isPending}
        onClose={() => setConfirmarAprobacion(false)}
        onConfirm={aprobarSeleccionados}
      >
        <Alert variant="warning" title="Envío a SUNAT">
          <span className={ICONO_EN_ALERTA}>
            <AlertTriangle className="size-4" aria-hidden="true" />
            Aprobar no emite el comprobante: lo deja en la cola que lo envía al servicio de
            facturación.
          </span>
        </Alert>
      </ConfirmDialog>
    </div>
  );
}
